<template>
  <div class="e1-plans">
    <h1 class="e1-plans__header">Get Started</h1>
    <div class="e1-plans__description">{{ description }}</div>
    <div v-loading="!pricingPlansLoaded" class="e1-plans__container">
      <template v-for="v in plansVisible">
        <e1-apple-podcast-item
          v-if="v.plan.planTypeString === 'applePodcasts'"
          :key="v.plan.planTypeString"
          :is-horizontal="isAPHorizontal"
        />
        <E1PlanItem
          v-else
          :key="v.plan.planTypeString"
          :plan="v.plan"
          @selectPlan="onSelectPlan"
        />
      </template>
      <div class="e1-plans__see-more-container">
        <a
          v-if="isMorePlansAvailable"
          role="button"
          tabindex="1"
          class="e1-plans__link"
          @click="onExtendPlans"
        >
          See More Plans
        </a>
      </div>
    </div>
    <div class="e1-plans__links">
      <a
        role="button"
        tabindex="0"
        class="e1-plans__link"
        @click="onContinueLimited"
      >
        Continue with Limited Access
      </a>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import E1PlanItem from './E1PlanItem';
import E1ApplePodcastItem from './E1ApplePodcastItem';
import countries from '@/assets/json/countries';
import { freePlan } from '../../shared/staticPlans';
import OptimizeService from '@/services/Optimize';
import { getServices } from '@/services/serviceProvider';

const [optimizeService] = getServices([OptimizeService]);

export default {
  name: 'E1PlanSignUp',
  components: {
    E1PlanItem,
    E1ApplePodcastItem,
  },
  data() {
    return {
      countries,
      isPlansExtended: false,
    };
  },
  computed: {
    ...mapGetters({
      countryCode: 'country',
      pricingPlansLoaded: 'pricingPlansLoaded',
      pricingPlans: 'pricingPlans',
      userSubscription: 'userSubscription',
      featureFlag: 'featureFlag',
      isHolidayPromoEnabled: 'isHolidayPromoEnabled',
    }),
    currentStep() {
      return (this.$route.meta && this.$route.meta.step) || 1;
    },
    currentProgress() {
      return this.currentStep / 4 * 100;
    },
    plansSorted() {
      const planOrder = {
        yearly: 1,
        'semi-annual': 2,
        monthly: 3,
        applePodcasts: 4,
      };
      const pricingPlans = [...this.pricingPlans].sort((a, b) => (planOrder[a.planTypeString] - planOrder[b.planTypeString]));
      return pricingPlans.map(plan => ({
        plan,
      }));
    },
    plansVisible() {
      return this.isPlansExtended
        ? this.plansSorted
        : this.plansVisibleInitially;
    },
    plansVisibleInitially() {
      return this.plansSorted.filter(p => (p.plan.planTypeString === 'yearly' || p.plan.planTypeString === 'applePodcasts'));
    },
    isAPHorizontal() {
      return (this.plansVisible.length - 1) % 3 === 0;
    },
    isMorePlansAvailable() {
      return !this.isPlansExtended && this.plansSorted.length > this.plansVisibleInitially.length;
    },
    description() {
      return this.isHolidayPromoEnabled ? 'Take advantage of our best price ever for the annual plan.' : 'Subscribe for full access to our network of award-winning Luminary Originals.';
    },
  },
  mounted() {
    this.setMetaInformation('Sign Up | Luminary', 'Get your Luminary subscription today & listen to original podcasts you won\'t find anywhere else. Start your 7-day free trial now!');
    const country = this.countries.find(v => v.code === this.countryCode) || this.countries.find(v => v.code === 'US');
    this.loadPricingPlans({ country: country.code, currency: country.currency });
  },
  destroyed() {
    this.setMetaInformation();
  },
  methods: {
    ...mapActions([
      'loadPricingPlans',
      'selectPricingPlan',
      'selectFreePlan',
    ]),
    ...mapActions('signupflow', [
      'setCompleted',
    ]),
    onSelectPlan(plan) {
      this.selectPlan(plan);
    },
    onExtendPlans() {
      optimizeService.triggerAnlyticsEvent({
        action: 'ShowMorePlans',
        category: 'Signup',
      });
      this.isPlansExtended = true;
    },
    onContinueLimited() {
      this.selectPlan(freePlan);
    },
    selectPlan(plan) {
      this.selectPricingPlan(plan);
      this.setCompleted('plan');
      optimizeService.triggerAnlyticsEvent({
        action: 'SelectPlan',
        category: 'Signup',
        label: plan.planTypeString,
        value: plan.paidPeriod,
      });
      this.$router.push(this.$route.meta.next);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.e1-plans {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  justify-content: center;
  flex-flow: column;
  &__header {
    font-family: $serif-bold;
    font-size: 36px;
    text-align: center;
  }
  &__description {
    font-size: 16px;
    text-align: center;
  }
  &__container {
    display: flex;
    align-content: flex-start;
    justify-content: center;
    flex-flow: row wrap;
    flex-grow: 1;
    width: 998px;
    @media only screen and (max-width: 998px) {
      width: initial;
      flex-flow: column;
    }
  }
  &__links {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    width: 100%;
    padding: 15px 20px;
    box-sizing: border-box;
  }
  &__link {
    font-size: 12px;
    color: #BBBBBB;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
  &__link-reg {
    font-size: 14px;
    color: $blue;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
  &__see-more-container {
    flex: 1 1 100%;
    text-align: center;
  }
}
</style>
